import React from 'react'
import { AppBar, Button, Grid, IconButton, List, ListItem, ListItemIcon, ListItemText, ListSubheader, Paper, TextField, Toolbar, Typography } from '@mui/material';
import { Box } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import TitleIcon from '@mui/icons-material/Title';
import TrashIcon from '@mui/icons-material/Delete';
import SubTopicTypeIcon from '@mui/icons-material/AccountTreeRounded';
import ReferenceTypeIcon from '@mui/icons-material/ForumRounded';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import ErrorPaper from '../components/ErrorPaper';
import InfoPaper from '../components/InfoPaper';
import AssociationDialog from '../components/AssociationDialog';

const Styles = {
    paper: {
        maxWidth: { xl: 1240, lg: 940, md: 640, sm: 480, xs: 320 },
        width: { xl: 1200, lg: 900, md: 600, sm: 480, xs: 320 },
        margin: '20px auto',
        overflow: 'hidden',
    },
    searchBar: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    searchInput: {
        fontSize: 'primary',
    },
    block: {
        display: 'block',
    },
    addTopic: {
        marginRight: 1,
    },
    contentWrapper: {
        margin: '40px 16px',
    },
    associations_list: {
        width: '100%',
        backgroundColor: 'paper',
    },
    nested: {
        paddingLeft: 4,
    },
};

enum TopicState {
    New = 1,
    Changed,
    Saved
}

type Association = {
    name: string,
    type: string
};

type Topic = {
    name: string,
    summary: string,
    associations: Association[],
    state: TopicState
};

const PensieveBaseUri = 'http://api.epensieve.com';

function TopicView() {
    const params = useParams();
    const { userId, token } = useAuth()!;
    const [loading, setLoading] = React.useState(true);
    const [openDlg, setOpenDlg] = React.useState(false);
    const [removeFlag, setRemoveFlag] = React.useState(false);
    const [filter, setFilter] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [info, setInfo] = React.useState<string | null>(null);
    const [topic, setTopic] = React.useState<Topic>({
        name: '',
        summary: '',
        associations: [],
        state: TopicState.New
    });

    const handleInputChange = (evt : any) => {
        const {id, value} = evt.target;
        setTopic({
            ...topic,
            [id]: value,
            state: (topic.state !== TopicState.New) ? TopicState.Changed : topic.state,
        });
    };

    const handleSubmit = (evt: any) => {
        evt.preventDefault();
        const { state, ...data } = topic;
        let url = `${PensieveBaseUri}/topics`;
        if (state === TopicState.Changed) {
            url += `/${encodeURI(topic.name)}`;
        }

        fetch( url, {
            method: (state === TopicState.Changed) ? 'PATCH' : 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(data)
        }).then((response) => {
            if (!response.ok || !response.body) {
                throw new Error(`Could not ${(state === TopicState.Changed) ? 'modify' : 'save'} Topic. Try Logging out an in again.`);
            }

            setTopic({
                ...data,
                state: TopicState.Saved
            });

            setInfo('Successfully saved changes to the Topic.');
            setTimeout(() => { setInfo(null); }, 2000);
        }).catch((err) => {
            setError(err.message);
        });
    };

    React.useEffect(() => {
        if(params.name) {
            let url = `${PensieveBaseUri}/topics/${encodeURI(params.name!)}`;
            if ( userId ) {
                url += `?userId=${encodeURI(userId)}`;
            }

            fetch( url, {method: 'GET'})
            .then( (response) => {
                if (!response.ok) {
                    throw new Error();
                }
    
                return response.json();
            })
            .then((result) => {
                setTopic({
                    ...result,
                    state: TopicState.Saved
                });
            })
            .catch(() => {
                setTopic({
                    name: params.name!,
                    summary: '',
                    associations: [],
                    state: TopicState.New
                });
            })
        }

        setLoading(false);
    }, [params.name, userId]);

    if (loading) {
        return (<Paper sx={Styles.paper} > <Typography> Loading Topics ... </Typography> </Paper>);
    }

    const readOnly = (userId == null);

    return (
        <form onSubmit={handleSubmit} >
        { (error) && <ErrorPaper error={error} /> }
        { (info) ? <InfoPaper msg={info} /> : <></> }
        <AssociationDialog mainTopicName={topic.name} associationName={filter} open={openDlg} onCancel={() => {setOpenDlg(false)}}
            onSubmit={ ( association : Association) => {
                topic.associations.push( association );
                setTopic(topic);
                setOpenDlg(false);
        }} />
        <Paper sx={Styles.paper} >
            <AppBar sx={Styles.searchBar} position='static' color='default' elevation={0} >
                <Toolbar>
                    <Grid container spacing={2} alignItems='center' >
                        <Grid item>
                            <TitleIcon />
                        </Grid>
                        <Grid item xs>
                            <TextField 
                                id='name'
                                sx={Styles.searchInput} 
                                fullWidth 
                                variant='standard' 
                                InputProps={{
                                    disableUnderline: true, 
                                    readOnly: readOnly || topic.state !== TopicState.New
                                }}
                                value={topic.name}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid item>
                            <Button variant='contained' type='submit' color='primary' >
                                {(topic.state === TopicState.New) ? 'Create' : 'Save'}
                            </Button>
                        </Grid>
                        <Grid item>
                            <IconButton component={RouterLink} to={`/topic/${encodeURI(topic.name)}`} >
                                <RefreshIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                </Toolbar>
            </AppBar>
            <Box sx={Styles.contentWrapper} >
                <Grid container direction='column' spacing={2} >
                    <Grid item>
                        <TextField
                            id='summary'
                            label='Summary'
                            variant='outlined'
                            disabled={readOnly}
                            multiline fullWidth
                            value={topic.summary}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={6} >
                        <Paper elevation={2} >
                        <AppBar sx={Styles.searchBar} position="static" color="default" elevation={0}>
                                <Toolbar>
                                    <Grid container spacing={1} alignItems="center">
                                        <Grid item sx={{marginRight:1, marginLeft:-1}} >
                                            <SearchIcon sx={Styles.block} color="inherit" />
                                        </Grid>
                                        <Grid item xs>
                                            <TextField
                                                fullWidth
                                                variant='standard'
                                                placeholder="Filter associations"
                                                onChange={ (evt) => { setFilter(evt.currentTarget?.value) } }
                                                sx = {Styles.searchInput}
                                                InputProps={{
                                                    disableUnderline: true
                                                }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Button 
                                                sx={Styles.addTopic}
                                                disabled={ readOnly }
                                                onClick={ () => { setOpenDlg(true); } }
                                                variant="contained" color="primary" >
                                                { "Add Association" }
                                            </Button>
                                        </Grid>
                                        <Grid item>
                                            <IconButton onClick={() => { setRemoveFlag(!removeFlag); }} >
                                                <TrashIcon sx={Styles.block} color='inherit' />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Toolbar>
                            </AppBar>
                            <List sx={Styles.associations_list}
                                aria-labelledby='nested-list-subheader'
                                subheader={
                                    <ListSubheader component='div' id='nested-list-subheader' >
                                        {
                                            (!removeFlag) ?
                                                `Current associations for this Topic ${(filter) ? `(Filtering by "${filter}")` : '' }`
                                            :
                                                "Select the associations you want to remove"
                                        }
                                    </ListSubheader>
                                }
                            > 
                                <ListItem key='subtopic' >
                                    <ListItemIcon sx={{fontSize: 'large', margin: '0px'}}>{ <SubTopicTypeIcon fontSize='inherit' /> }</ListItemIcon>
                                    <ListItemText sx={{margin:'0px -20px'}} primaryTypographyProps={{ fontSize: 18, fontWeight: 'medium', letterSpacing: 0 }} >
                                        { 'Subtopics' }
                                    </ListItemText>
                                </ListItem>
                            { topic.associations.filter(({name, type}) => (type === 'subtopic') && (name.toUpperCase().startsWith(filter?.toUpperCase() || '')) ).map((association) => (
                                    <ListItem 
                                        key={association.name} button 
                                        sx={ { 
                                            ...Styles.nested,
                                            '&:hover': {
                                                backgroundColor: (removeFlag) ? '#ff8080' : 'inherit',
                                            },
                                        }}
                                        onClick={(evt : any) => { 
                                            if (removeFlag) { 
                                                evt.preventDefault();
                                                setTopic({
                                                    ...topic,
                                                    associations: topic.associations.filter((item) => (item.name !== association.name)),
                                                    state: (topic.state !== TopicState.New) ? TopicState.Changed : TopicState.New
                                                });
                                            }
                                        }}
                                        component={RouterLink} to={`/topic/${encodeURI(association.name)}`} >
                                        <ListItemText primary={association.name} />
                                    </ListItem>
                            ))}
                                <ListItem key='reference' >
                                    <ListItemIcon sx={{fontSize: 'large', margin: '0px'}}>{ <ReferenceTypeIcon fontSize='inherit' /> }</ListItemIcon>
                                    <ListItemText sx={{margin:'0px -20px'}} primaryTypographyProps={{ fontSize: 18, fontWeight: 'medium', letterSpacing: 0 }} >
                                        { 'References' }
                                    </ListItemText>
                                </ListItem>
                            { topic.associations.filter(({name, type}) => (type === 'reference') && (name.toUpperCase().startsWith(filter?.toUpperCase() || ''))).map((association) => (
                                    <ListItem 
                                        key={association.name} button 
                                        sx={ { 
                                            ...Styles.nested,
                                            '&:hover': {
                                                backgroundColor: (removeFlag) ? '#ff8080' : 'inherit',
                                            },
                                        }}
                                        onClick={(evt : any) => { 
                                            if (removeFlag) { 
                                                evt.preventDefault();
                                                setTopic({
                                                    ...topic,
                                                    associations: topic.associations.filter((item) => (item.name !== association.name)),
                                                    state: (topic.state !== TopicState.New) ? TopicState.Changed : TopicState.New
                                                });
                                            }
                                        }}
                                        component={RouterLink} to={`/topic/${encodeURI(association.name)}`} >
                                        <ListItemText primary={association.name} />
                                    </ListItem>
                            ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
        </form>
    )
}

export default TopicView